import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowsRightLeftIcon,
  CalculatorIcon,
  ChartBarIcon,
  ClockIcon,
  TableCellsIcon,
  ViewColumnsIcon
} from '@heroicons/react/24/outline'
import PageHeader from '../layout/PageHeader'
import Badge from '../ui/Badge'
import Card from '../ui/Card'
import Button from '../ui/Button'
import BackButton from '../ui/BackButton'
import PageLoader from '../ui/PageLoader'
import {
  fetchMinMaxOverrides,
  fetchWeldTelemetryAggregate,
  fetchWeldTelemetrySamples,
  reassignSamples,
  saveMinMaxOverrides,
  type FetchWeldTelemetrySamplesResult,
  type MinMaxOverrideInput,
  type MinMaxOverrideRecord,
  type WeldTelemetrySampleRow
} from '../../utils/weldTelemetryClient'
import { useProjects } from '../../contexts/ProjectsContext'
import {
  PASS_ANALYTICS_ALL_COLUMN_KEYS,
  PASS_ANALYTICS_DEFAULT_COLUMN_KEYS,
  getPassAnalyticsColumnGroups,
  PassAnalyticsTable
} from './PassAnalyticsTable'
import PassAnalyticsGraph from './PassAnalyticsGraph'
import PassAnalyticsColumnSelectorDialog from './PassAnalyticsColumnSelectorDialog'
import MinMaxConfirmDialog from './MinMaxConfirmDialog'
import MoveToPassDialog from './MoveToPassDialog'
import { getParameterUnitSuffix } from '../../utils/weldUnitUtils'
import { useUnitSetting } from '../../utils/userUnitSetting'
import {
  computeAllStats,
  applyToleranceToAllStats,
  NULL_STATS,
  STATS_COLUMN_KEYS,
  type ParamStats,
  type StatsColumnKey
} from '../../utils/passAnalyticsStats'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { aggregateFromServerResponse, type AggregateResult } from '../../utils/weldParameterAggregator'
import { buildPreviousStatsForPass } from '../../utils/passAnalyticsBaselineStats'
import {
  readLimitsModeFromWeldSection,
  readToleranceFromWeldSection
} from '../../utils/pqr/weldParamsWpsLimits'
import { formatTolerancePercent } from '../../utils/pqr/applyPqrTolerance'
import ConfirmDialog from '../ui/ConfirmDialog'
import { useData360FloorSheet } from '../../hooks/useData360FloorSheet'
import { DATA_360_DISPLAY_NAME, formatData360DisplayText } from '../../utils/data360DisplayName'
import {
  data360ReimportConfirmDescription,
  readData360MetaFromWeldSection,
  samplesMissingBugType
} from '../../utils/passAnalyticsBugType'
import {
  expandPassSequenceToLabels,
  passSectionInputForBevel
} from '../../utils/passSequenceExpanded'
import { normalizeBevelIndex } from '../../utils/pupHeatCatalog'

type TabKey = 'table' | 'graph'

/**
 * localStorage key (per project / pipe set / weld / pass label) for the
 * "Columns" button's visible-key selection. Mirrors the lightweight Data360
 * `NdtRepairLogModal` persistence shape — no backend round-trip required.
 */
function columnsStorageKey(
  projectId: string,
  pipeId: string,
  weldId: string,
  passLabel: string
): string {
  return `wps-studio:pass-analytics-columns:${projectId}:${pipeId}:${weldId}:${passLabel}`
}

/**
 * sessionStorage key (per project/pipeSet/weld/pass) for the table column
 * filter draft. Survives tab refresh in the same session, but clears when
 * the user closes the browser — which is the contract the user picked in
 * the clarifying questions.
 */
function filtersStorageKey(
  projectId: string,
  pipeId: string,
  weldId: string,
  passLabel: string
): string {
  return `wps-studio:pass-analytics-filters:${projectId}:${pipeId}:${weldId}:${passLabel}`
}

function readPersistedColumns(key: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    const allowed = new Set(PASS_ANALYTICS_ALL_COLUMN_KEYS)
    const filtered = parsed.filter((k): k is string => typeof k === 'string' && allowed.has(k))
    return filtered.length > 0 ? filtered : null
  } catch {
    return null
  }
}

function writePersistedColumns(key: string, keys: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(keys))
  } catch {
    // Storage may be unavailable (private mode, quota); we silently no-op so
    // the UI keeps working from in-memory state.
  }
}

/**
 * Pass Analytics page — opened from the eye icon on the Welding Parameters
 * (uploaded view) table. Shows sample-level telemetry for one (weld, pass)
 * combination. Issue 02 ships the Table tab body; later slices add unit
 * conversion (issue 03), the Graph tab body (issue 04), and table polish
 * (issue 05).
 */
export default function PassAnalyticsPage() {
  const { id, pipeId, weldId, passLabel: rawPassLabel } = useParams()
  useUnitSetting()
  const unitProbe = getParameterUnitSuffix('travelSpeed')
  const passAnalyticsColumnGroups = useMemo(
    () => getPassAnalyticsColumnGroups(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitProbe]
  )
  const passLabel = rawPassLabel ? decodeURIComponent(rawPassLabel) : ''
  const { getProject } = useProjects()
  const { getSection, updateSection, sections, refresh, syncSectionsVersion } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((p) => p.id === pipeId)
  const data360 = useData360FloorSheet(id, pipeId, project?.data360ProjectId)

  const weldBevelIndex = useMemo(() => {
    if (!weldId) return null
    const floorData = getSection('floorsheet')
    const welds = floorData && Array.isArray(floorData.welds) ? floorData.welds : []
    const currentWeld = welds.find(
      (w: unknown) => String((w as { id?: unknown })?.id) === String(weldId)
    )
    if (!currentWeld || typeof currentWeld !== 'object') return null
    return normalizeBevelIndex((currentWeld as { bevelIndex?: unknown }).bevelIndex)
  }, [getSection, sections, weldId])

  const wpsPassLabels = useMemo(() => {
    const passSection = getSection('pass')
    const bevelInput =
      weldBevelIndex !== null
        ? passSectionInputForBevel(passSection, weldBevelIndex)
        : passSectionInputForBevel(passSection, 0)
    return expandPassSequenceToLabels(bevelInput)
  }, [getSection, weldBevelIndex, sections])

  const [data, setData] = useState<FetchWeldTelemetrySamplesResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [tab, setTab] = useState<TabKey>('table')
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false)
  /**
   * The set of sample IDs currently checked in the table. Defaults to "all
   * sample IDs" once `data` resolves and is reset whenever new data lands.
   */
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<number>>(() => new Set())
  /** Open-state for the per-pass Min/Max persistence confirmation dialog. */
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [savingMinMax, setSavingMinMax] = useState(false)
  /**
   * Persisted Min/Max overrides loaded from the server. Kept on state so the
   * `Calculate min/max → Confirm & Save` flow can echo the latest snapshot
   * back into the dialog. The TABLE HEADER now reads from `liveStats` so the
   * Min/Avg/Max values appear automatically as soon as the table loads —
   * persisted overrides only drive what's pre-checked / unchecked on first
   * mount (see `unselectedSampleIds`).
   */
  const [persistedOverrides, setPersistedOverrides] = useState<MinMaxOverrideRecord[]>([])
  const [overridesLoaded, setOverridesLoaded] = useState(false)
  /** Telemetry aggregate for baseline Previous stats (Welding Parameters display). */
  const [telemetryAggregate, setTelemetryAggregate] = useState<AggregateResult | null>(null)
  const [aggregateLoaded, setAggregateLoaded] = useState(false)
  const [showData360ReimportConfirm, setShowData360ReimportConfirm] = useState(false)
  const [reimportBusy, setReimportBusy] = useState(false)
  /** Session sticky: after a successful re-import that still lacks bugType. */
  const [bugTypeStillMissingAfterReimport, setBugTypeStillMissingAfterReimport] = useState(false)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [movingSamples, setMovingSamples] = useState(false)
  /**
   * Saved-on-server set of UNSELECTED sample IDs, restored from the latest
   * Calculate min/max snapshot. Empty when no save has happened yet. We hold
   * this in a ref so the samples fetch can decide whether to default-check
   * every row or restore the saved selection once data arrives — without
   * adding another effect that re-runs on every state change.
   */
  const persistedUnselectedRef = useRef<number[] | null>(null)

  const storageKey = useMemo(
    () =>
      id && pipeId && weldId && passLabel ? columnsStorageKey(id, pipeId, weldId, passLabel) : '',
    [id, pipeId, weldId, passLabel]
  )

  const filtersKey = useMemo(
    () =>
      id && pipeId && weldId && passLabel ? filtersStorageKey(id, pipeId, weldId, passLabel) : '',
    [id, pipeId, weldId, passLabel]
  )

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(() =>
    storageKey
      ? readPersistedColumns(storageKey) ?? PASS_ANALYTICS_DEFAULT_COLUMN_KEYS
      : PASS_ANALYTICS_DEFAULT_COLUMN_KEYS
  )

  // Re-hydrate visibility whenever the (weld, pass) target changes — each
  // (weld, pass) keeps its own preference under a distinct storage key.
  useEffect(() => {
    if (!storageKey) return
    const persisted = readPersistedColumns(storageKey)
    setVisibleColumnKeys(persisted ?? PASS_ANALYTICS_DEFAULT_COLUMN_KEYS)
  }, [storageKey])

  /**
   * Compute the default selection set for a fresh sample list given the
   * persisted "unselected" IDs from the last save (may be `null` while the
   * overrides fetch is still in flight). When `persisted` is `null` we use a
   * permissive default (all selected); the second fetch will refine it once
   * persisted state lands.
   */
  const buildDefaultSelection = useCallback(
    (samples: WeldTelemetrySampleRow[], persisted: number[] | null): Set<number> => {
      if (!persisted || persisted.length === 0) {
        return new Set(samples.map((s) => s.sampleId))
      }
      const skip = new Set(persisted)
      const out = new Set<number>()
      for (const s of samples) {
        if (!skip.has(s.sampleId)) out.add(s.sampleId)
      }
      return out
    },
    []
  )

  const runFetch = useCallback(
    (signal?: AbortSignal): Promise<FetchWeldTelemetrySamplesResult | null> => {
      if (!id || !pipeId || !weldId || !passLabel) return Promise.resolve(null)
      setLoading(true)
      setError(null)
      return fetchWeldTelemetrySamples({
        projectId: id,
        pipeSetId: pipeId,
        weldId,
        passLabel
      })
        .then((result) => {
          if (signal?.aborted) return null
          setData(result)
          setSelectedSampleIds(
            buildDefaultSelection(result.samples, persistedUnselectedRef.current)
          )
          setLoading(false)
          return result
        })
        .catch((err) => {
          if (signal?.aborted) return null
          setError(err instanceof Error ? err : new Error(String(err)))
          setLoading(false)
          return null
        })
    },
    [id, pipeId, weldId, passLabel, buildDefaultSelection]
  )

  useEffect(() => {
    persistedUnselectedRef.current = null
    setBugTypeStillMissingAfterReimport(false)
    const controller = new AbortController()
    void runFetch(controller.signal)
    return () => controller.abort()
  }, [runFetch])

  // Persisted overrides + selection snapshot. Loaded once per (weld, pass)
  // target; refreshed on save below. Once the overrides land we also re-apply
  // the saved selection to the currently loaded sample set so users see the
  // same checked/unchecked state they left behind.
  useEffect(() => {
    if (!id || !pipeId || !weldId || !passLabel) return
    let cancelled = false
    setOverridesLoaded(false)
    fetchMinMaxOverrides({ projectId: id, pipeSetId: pipeId, weldId, passLabel })
      .then((result) => {
        if (cancelled) return
        setPersistedOverrides(result.overrides)
        persistedUnselectedRef.current = result.unselectedSampleIds
        // If the samples already landed before the overrides did, refresh
        // the selection set so the unsaved rows show up unchecked.
        setData((prev) => {
          if (prev) {
            setSelectedSampleIds(
              buildDefaultSelection(prev.samples, result.unselectedSampleIds)
            )
          }
          return prev
        })
        setOverridesLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        // Soft-fail: an empty overrides list just means the header reads
        // straight from the live stats with every row default-selected.
        setPersistedOverrides([])
        persistedUnselectedRef.current = []
        setOverridesLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [id, pipeId, weldId, passLabel, buildDefaultSelection])

  // Load weld telemetry aggregate so the Recalculate dialog can show Welding
  // Parameters values as "Previous" before the first save.
  useEffect(() => {
    if (!id || !pipeId || !weldId) return
    let cancelled = false
    setAggregateLoaded(false)

    const cached = getSection(`weldparams.${weldId}`)
    const cachedAggregate =
      cached &&
      typeof cached === 'object' &&
      cached.aggregate &&
      typeof cached.aggregate === 'object' &&
      Array.isArray((cached.aggregate as AggregateResult).rows)
        ? (cached.aggregate as AggregateResult)
        : null

    if (cachedAggregate) {
      setTelemetryAggregate(cachedAggregate)
      setAggregateLoaded(true)
      return
    }

    fetchWeldTelemetryAggregate({ projectId: id, pipeSetId: pipeId, weldId })
      .then((remote) => {
        if (cancelled) return
        setTelemetryAggregate(aggregateFromServerResponse(remote))
        setAggregateLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setTelemetryAggregate(null)
        setAggregateLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [id, pipeId, weldId, getSection, sections])

  const toggleSample = useCallback((sampleId: number) => {
    setSelectedSampleIds((prev) => {
      const next = new Set(prev)
      if (next.has(sampleId)) next.delete(sampleId)
      else next.add(sampleId)
      return next
    })
  }, [])

  const toggleAllSamples = useCallback(() => {
    setSelectedSampleIds((prev) => {
      if (!data) return prev
      if (prev.size === data.samples.length) return new Set<number>()
      return new Set(data.samples.map((s) => s.sampleId))
    })
  }, [data])

  // Samples currently included in the live computation. Drives BOTH the
  // header Min/Avg/Max stats and the Graph tab so toggling a checkbox is
  // immediately reflected everywhere — matching the "real time" expectation
  // for Pass Analytics.
  const filteredSamples = useMemo<WeldTelemetrySampleRow[]>(() => {
    if (!data) return []
    return data.samples.filter((s) => selectedSampleIds.has(s.sampleId))
  }, [data, selectedSampleIds])

  const liveStats = useMemo<Record<StatsColumnKey, ParamStats>>(
    () => computeAllStats(filteredSamples),
    [filteredSamples]
  )

  const weldParamsSection = useMemo(
    () => (weldId ? getSection(`weldparams.${weldId}`) : null),
    [getSection, weldId, sections]
  )
  const data360Meta = useMemo(
    () => readData360MetaFromWeldSection(weldParamsSection),
    [weldParamsSection]
  )
  const missingBugType = useMemo(
    () => (data ? samplesMissingBugType(data.samples) : false),
    [data]
  )
  const showBugTypeReimportBanner =
    Boolean(data360Meta) && missingBugType && !bugTypeStillMissingAfterReimport
  const showBugTypeMissingInfo =
    Boolean(data360Meta) && missingBugType && bugTypeStillMissingAfterReimport

  const handleData360Reimport = useCallback(async () => {
    if (!id || !pipeId || !weldId || !data360Meta) return
    setReimportBusy(true)
    try {
      const result = await data360.runWeldImport({
        weldRowId: weldId,
        productionWeldId: data360Meta.productionWeldId,
        weldName: data360Meta.weldName || weldId,
        jobNumber: data360Meta.jobNumber
      })
      syncSectionsVersion(result.sectionsVersion)
      await refresh()
      const samplesResult = await runFetch()
      setShowData360ReimportConfirm(false)
      if (samplesResult && samplesMissingBugType(samplesResult.samples)) {
        setBugTypeStillMissingAfterReimport(true)
        toast.success(
          result.replaced
            ? `Replaced weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
            : `Imported weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
        )
      } else {
        setBugTypeStillMissingAfterReimport(false)
        toast.success(
          result.replaced
            ? `Replaced weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
            : `Imported weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
        )
      }
    } catch (err: unknown) {
      toast.error(
        formatData360DisplayText(
          err instanceof Error ? err.message : `Failed to re-import from ${DATA_360_DISPLAY_NAME}.`
        )
      )
    } finally {
      setReimportBusy(false)
    }
  }, [id, pipeId, weldId, data360Meta, data360, syncSectionsVersion, refresh, runFetch])

  const toleranceBoundary = useMemo(
    () => readToleranceFromWeldSection(weldParamsSection),
    [weldParamsSection]
  )
  const pqrLimitsMode = useMemo(
    () => readLimitsModeFromWeldSection(weldParamsSection),
    [weldParamsSection]
  )
  const displayStats = useMemo(
    () => applyToleranceToAllStats(liveStats, toleranceBoundary, pqrLimitsMode),
    [liveStats, toleranceBoundary, pqrLimitsMode]
  )

  /**
   * Sample IDs the user has currently UNSELECTED. Persisted alongside the
   * Min/Avg/Max snapshot so reopening the file restores the same checkbox
   * state.
   */
  const unselectedSampleIds = useMemo<number[]>(() => {
    if (!data) return []
    const out: number[] = []
    for (const s of data.samples) {
      if (!selectedSampleIds.has(s.sampleId)) out.push(s.sampleId)
    }
    return out
  }, [data, selectedSampleIds])

  /**
   * Stats rendered in the table header. We always show the LIVE computation
   * for the current selection so the header populates automatically the
   * first time a TXT upload is opened (no need to click Calculate min/max
   * just to see Min/Avg/Max). After the user saves, the selection state is
   * persisted and restored on reopen — which means the live header
   * implicitly matches the saved snapshot the next time the page loads.
   *
   * `persistedStats` is passed to the recalculate confirmation dialog so the
   * user can compare previous saved values against the live recomputation.
   */
  const baselineStats = useMemo(
    () =>
      buildPreviousStatsForPass({
        persistedOverrides,
        aggregate: telemetryAggregate,
        passLabel
      }),
    [persistedOverrides, telemetryAggregate, passLabel]
  )
  const displayPreviousStats = useMemo(
    () => applyToleranceToAllStats(baselineStats, toleranceBoundary, pqrLimitsMode),
    [baselineStats, toleranceBoundary, pqrLimitsMode]
  )

  const handleOpenConfirm = useCallback(() => {
    if (!data || filteredSamples.length === 0) return
    setConfirmOpen(true)
  }, [data, filteredSamples.length])

  const handleConfirmMinMax = useCallback(async () => {
    if (!id || !pipeId || !weldId || !passLabel) return
    setSavingMinMax(true)
    try {
      const overrides: MinMaxOverrideInput[] = STATS_COLUMN_KEYS.map((key) => {
        const s = liveStats[key]
        return {
          parameter: key,
          min: s?.min ?? null,
          avg: s?.avg ?? null,
          max: s?.max ?? null,
          sampleCount: filteredSamples.length
        }
      })
      const result = await saveMinMaxOverrides({
        projectId: id,
        pipeSetId: pipeId,
        weldId,
        passLabel,
        overrides,
        unselectedSampleIds
      })
      // Refresh the persisted snapshot so the save flow is idempotent and
      // any future reload starts from the just-saved state.
      setPersistedOverrides(result.overrides)
      persistedUnselectedRef.current = result.unselectedSampleIds
      try {
        const remote = await fetchWeldTelemetryAggregate({
          projectId: id,
          pipeSetId: pipeId,
          weldId
        })
        const aggregate = aggregateFromServerResponse(remote)
        setTelemetryAggregate(aggregate)
        const existingSection = getSection(`weldparams.${weldId}`)
        const existingRecord =
          existingSection && typeof existingSection === 'object'
            ? (existingSection as Record<string, unknown>)
            : {}
        const existingData360 =
          existingRecord.data360 && typeof existingRecord.data360 === 'object'
            ? (existingRecord.data360 as Record<string, unknown>)
            : undefined
        await updateSection(`weldparams.${weldId}`, {
          ...existingRecord,
          source: 'upload',
          aggregate,
          sourceFilename:
            remote.sourceFilename || String(existingRecord.sourceFilename || ''),
          uploadedAt: remote.uploadedAt || String(existingRecord.uploadedAt || ''),
          passMappings:
            remote.passMappings && typeof remote.passMappings === 'object'
              ? remote.passMappings
              : existingRecord.passMappings,
          ...(existingData360 ? { data360: existingData360 } : {})
        })
      } catch (refreshErr) {
        toast.error(
          refreshErr instanceof Error
            ? refreshErr.message
            : 'Saved, but Welding Parameters cache could not be refreshed'
        )
      }
      toast.success(
        `Min/Max updated from ${filteredSamples.length.toLocaleString()} samples for ${passLabel}`
      )
      setConfirmOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save Min/Max')
    } finally {
      setSavingMinMax(false)
    }
  }, [
    id,
    pipeId,
    weldId,
    passLabel,
    liveStats,
    filteredSamples.length,
    unselectedSampleIds,
    getSection,
    updateSection
  ])

  const openMoveDialog = useCallback(() => {
    setMoveDialogOpen(true)
  }, [])

  const handleConfirmMove = useCallback(
    async (input: { targetPassLabel: string; sampleIds: number[] }) => {
      if (!id || !pipeId || !weldId || !passLabel) return
      setMovingSamples(true)
      try {
        const result = await reassignSamples({
          projectId: id,
          pipeSetId: pipeId,
          weldId,
          sourcePassLabel: passLabel,
          targetPassLabel: input.targetPassLabel,
          sampleIds: input.sampleIds
        })
        toast.success(
          `Moved ${result.movedCount.toLocaleString()} sample${
            result.movedCount === 1 ? '' : 's'
          } to ${input.targetPassLabel}`
        )
        setMoveDialogOpen(false)
        // Min/max overrides were cleared server-side for both passes.
        setPersistedOverrides([])
        persistedUnselectedRef.current = []
        setSelectedSampleIds(new Set())
        await runFetch()
        try {
          const aggregate = await fetchWeldTelemetryAggregate({
            projectId: id,
            pipeSetId: pipeId,
            weldId
          })
          setTelemetryAggregate(aggregateFromServerResponse(aggregate as any))
        } catch {
          // Aggregate refresh is best-effort; samples already reloaded.
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to move samples')
      } finally {
        setMovingSamples(false)
      }
    },
    [id, pipeId, weldId, passLabel, runFetch]
  )

  const tableSelection = useMemo(
    () => ({
      selectedSampleIds,
      onToggle: toggleSample,
      onToggleAll: toggleAllSamples
    }),
    [selectedSampleIds, toggleSample, toggleAllSamples]
  )

  const backHref =
    id && pipeId && weldId
      ? `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(
          pipeId
        )}/floorsheet/${encodeURIComponent(weldId)}/parameters?sync=1`
      : '#'

  return (
    <div className="flex w-full min-w-0 flex-col space-y-5">
      <PageHeader
        backTo={{ to: backHref, label: 'Back to Welding Parameters' }}
        title="Pass Analytics"
        subtitle={
          <span className="flex min-w-0 items-center gap-x-2 truncate">
            {pipeSet ? (
              <span className="flex-shrink-0">
                {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
                {pipeSet.wallThicknessUnit}
              </span>
            ) : null}
            {pipeSet ? <span className="text-[#E2E5EA]">•</span> : null}
            <Badge tone="primary">{passLabel}</Badge>
            {data ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <span className="flex-shrink-0">
                  {data.sampleCount.toLocaleString()} samples
                </span>
              </>
            ) : null}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {!overridesLoaded ? (
              <span
                className="text-[11px] text-heritage-grey"
                data-testid="pa-overrides-loading"
              >
                Loading saved values…
              </span>
            ) : null}
            <button
              type="button"
              data-testid="pa-move-to-pass"
              onClick={() => openMoveDialog()}
              disabled={!data || data.samples.length === 0 || movingSamples}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-blueprint-blue hover:bg-page-bg hover:text-blueprint-blue disabled:cursor-not-allowed disabled:border-[#E2E5EA] disabled:bg-gray-100 disabled:text-heritage-grey"
            >
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" aria-hidden />
              Move to pass
            </button>
            <button
              type="button"
              data-testid="pa-calculate-minmax"
              onClick={handleOpenConfirm}
              disabled={!data || filteredSamples.length === 0 || !overridesLoaded}
              className="inline-flex items-center gap-1.5 rounded-md border border-hero-red bg-hero-red px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-hero-red-60 disabled:cursor-not-allowed disabled:border-[#E2E5EA] disabled:bg-gray-100 disabled:text-heritage-grey"
            >
              <CalculatorIcon className="h-3.5 w-3.5" aria-hidden />
              Recalculate
            </button>
            <button
              type="button"
              data-testid="pa-columns-button"
              onClick={() => setColumnsDialogOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-blueprint-blue hover:bg-page-bg hover:text-blueprint-blue"
            >
              <ViewColumnsIcon className="h-3.5 w-3.5" aria-hidden />
              Columns
            </button>
          </div>
        }
      />

      {toleranceBoundary > 0 ? (
        <div
          data-testid="pa-tolerance-banner"
          className="rounded-lg border border-warning-200 bg-warning-50/80 px-4 py-2.5 text-sm text-warning-800"
        >
          <span className="font-semibold">
            {formatTolerancePercent(toleranceBoundary)} tolerance applied
          </span>
          <span className="text-warning-700">
            {' '}
            — displayed values match Welding Parameters.
          </span>
        </div>
      ) : null}

      {showBugTypeReimportBanner ? (
        <div
          data-testid="pa-bugtype-reimport-banner"
          className="flex flex-col gap-3 rounded-lg border border-warning-200 bg-warning-50/80 px-4 py-3 text-sm text-warning-800 sm:flex-row sm:items-center sm:justify-between"
        >
          <p>
            <span className="font-semibold">Bug Type (CW/CCW) is missing</span>
            <span className="text-warning-700">
              {' '}
              for this pass. Re-import the weld from {DATA_360_DISPLAY_NAME} to refresh telemetry.
            </span>
          </p>
          <button
            type="button"
            data-testid="pa-bugtype-reimport"
            disabled={reimportBusy}
            onClick={() => setShowData360ReimportConfirm(true)}
            className="inline-flex flex-shrink-0 items-center justify-center rounded-md border border-hero-red bg-hero-red px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-hero-red-60 disabled:cursor-not-allowed disabled:border-[#E2E5EA] disabled:bg-gray-100 disabled:text-heritage-grey"
          >
            Re-import
          </button>
        </div>
      ) : null}

      {showBugTypeMissingInfo ? (
        <div
          data-testid="pa-bugtype-missing-info"
          className="rounded-lg border border-blue-100 bg-blue-50/70 px-4 py-2.5 text-sm text-blueprint-blue"
        >
          Bug Type (CW/CCW) is not present in the {DATA_360_DISPLAY_NAME} import for this weld.
        </div>
      ) : null}

      <div className="flex items-center justify-between border-b border-[#E2E5EA]">
        <div role="tablist" aria-label="Pass Analytics view" className="flex items-center gap-1">
          {(['table', 'graph'] as TabKey[]).map((key) => {
            const active = tab === key
            const Icon = key === 'table' ? TableCellsIcon : ChartBarIcon
            return (
              <button
                key={key}
                role="tab"
                type="button"
                aria-selected={active}
                data-testid={`pa-tab-${key}`}
                onClick={() => setTab(key)}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? 'border-blueprint-blue text-blueprint-blue'
                    : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {key === 'table' ? 'Table' : 'Graph'}
              </button>
            )
          })}
        </div>
        <div
          data-testid="pa-utc-banner"
          className="mb-1 inline-flex items-center gap-1.5 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-1 text-[11px] font-medium text-blueprint-blue"
        >
          <ClockIcon className="h-3.5 w-3.5" aria-hidden />
          Times shown in UTC
        </div>
      </div>

      {tab === 'table' ? (
        <TableTab
          loading={loading}
          error={error}
          data={data}
          passLabel={passLabel}
          onRetry={() => {
            void runFetch()
          }}
          backHref={backHref}
          visibleColumnKeys={visibleColumnKeys}
          headerStats={displayStats}
          toleranceBoundary={toleranceBoundary}
          selection={tableSelection}
          filtersStorageKey={filtersKey}
        />
      ) : (
        <GraphTab
          loading={loading}
          error={error}
          samples={data?.samples ?? []}
          selectedSampleIds={selectedSampleIds}
          sourceFilenames={data?.sourceFilenames}
          totalSampleCount={data?.samples.length ?? 0}
          toleranceBoundary={toleranceBoundary}
          onRetry={() => {
            void runFetch()
          }}
        />
      )}

      <PassAnalyticsColumnSelectorDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        groups={passAnalyticsColumnGroups}
        visibleKeys={visibleColumnKeys}
        defaultKeys={PASS_ANALYTICS_DEFAULT_COLUMN_KEYS}
        onApply={(keys) => {
          setVisibleColumnKeys(keys)
          if (storageKey) writePersistedColumns(storageKey, keys)
          setColumnsDialogOpen(false)
        }}
      />

      <MinMaxConfirmDialog
        open={confirmOpen}
        onClose={() => (savingMinMax ? undefined : setConfirmOpen(false))}
        onConfirm={() => void handleConfirmMinMax()}
        passLabel={passLabel}
        sampleCount={filteredSamples.length}
        totalSampleCount={data?.samples.length ?? 0}
        stats={displayStats}
        previousStats={displayPreviousStats}
        previousLoading={!overridesLoaded || !aggregateLoaded}
        busy={savingMinMax}
      />

      <MoveToPassDialog
        open={moveDialogOpen}
        onClose={() => {
          if (!movingSamples) setMoveDialogOpen(false)
        }}
        onConfirm={(input) => void handleConfirmMove(input)}
        sourcePassLabel={passLabel}
        wpsPassLabels={wpsPassLabels}
        samples={data?.samples ?? []}
        sourceFilenames={data?.sourceFilenames}
        busy={movingSamples}
      />

      <ConfirmDialog
        open={showData360ReimportConfirm}
        title={`Re-import from ${DATA_360_DISPLAY_NAME}?`}
        description={data360ReimportConfirmDescription(
          data360Meta?.weldName || weldId || 'this weld'
        )}
        confirmLabel="Re-import"
        cancelLabel="Cancel"
        tone="danger"
        confirmBusy={reimportBusy}
        onConfirm={() => void handleData360Reimport()}
        onCancel={() => (reimportBusy ? undefined : setShowData360ReimportConfirm(false))}
      />
    </div>
  )
}

interface TableTabProps {
  loading: boolean
  error: Error | null
  data: FetchWeldTelemetrySamplesResult | null
  passLabel: string
  backHref: string
  onRetry: () => void
  visibleColumnKeys: string[]
  headerStats: Record<StatsColumnKey, ParamStats> | null
  selection: {
    selectedSampleIds: Set<number>
    onToggle: (sampleId: number) => void
    onToggleAll: () => void
  }
  filtersStorageKey: string
  toleranceBoundary?: number
}

function TableTab({
  loading,
  error,
  data,
  passLabel,
  backHref,
  onRetry,
  visibleColumnKeys,
  headerStats,
  selection,
  filtersStorageKey,
  toleranceBoundary = 0
}: TableTabProps) {
  if (loading) {
    return (
      <div data-testid="pa-loading">
        <PageLoader label={`Loading samples for ${passLabel}…`} />
      </div>
    )
  }

  if (error) {
    return (
      <div
        data-testid="pa-error"
        className="flex items-center justify-between gap-3 rounded-lg border border-error-200 bg-error-50/40 px-4 py-3 text-sm text-error-700"
      >
        <span>Failed to load samples — {error.message || 'Please try again'}</span>
        <Button onClick={onRetry} variant="secondary">
          Retry
        </Button>
      </div>
    )
  }

  if (!data || data.samples.length === 0) {
    return (
      <Card>
        <div
          data-testid="pa-empty"
          className="flex flex-col items-center justify-center gap-3 py-10 text-center"
        >
          <p className="text-sm font-semibold text-heritage-grey">
            No telemetry rows for pass &quot;{passLabel}&quot;
          </p>
          <p className="text-xs text-text-grey">
            Re-upload the TXT if you expected data here.
          </p>
          <BackButton to={backHref} tooltip="Back to Welding Parameters" />
        </div>
      </Card>
    )
  }

  return (
    <PassAnalyticsTable
      samples={data.samples}
      passLabel={passLabel}
      visibleKeys={visibleColumnKeys}
      headerStats={headerStats ?? undefined}
      selection={selection}
      filtersStorageKey={filtersStorageKey}
      sourceFilenames={data.sourceFilenames}
      toleranceBoundary={toleranceBoundary}
    />
  )
}

interface GraphTabProps {
  loading: boolean
  error: Error | null
  /** Full pass samples — graph splits boundaries on this list. */
  samples: WeldTelemetrySampleRow[]
  /** Live checkbox selection — graph plots only these IDs. */
  selectedSampleIds: Set<number>
  sourceFilenames?: string[]
  /** Original sample count used to detect "all unchecked" vs "no data at all". */
  totalSampleCount: number
  toleranceBoundary?: number
  onRetry: () => void
}

function GraphTab({
  loading,
  error,
  samples,
  selectedSampleIds,
  sourceFilenames,
  totalSampleCount,
  toleranceBoundary = 0,
  onRetry
}: GraphTabProps) {
  if (loading) {
    return (
      <PageLoader label="Loading graph…" compact data-testid="pa-graph-loading" />
    )
  }

  if (error) {
    return (
      <div
        data-testid="pa-graph-error"
        className="flex items-center justify-between gap-3 rounded-lg border border-error-200 bg-error-50/40 px-4 py-3 text-sm text-error-700"
      >
        <span>Failed to load samples — {error.message || 'Please try again'}</span>
        <Button onClick={onRetry} variant="secondary">
          Retry
        </Button>
      </div>
    )
  }

  // When at least one sample exists in the source dataset but every row is
  // currently unchecked, the chart would otherwise render the same empty
  // state as "no data at all". Show a more informative banner instead so the
  // user knows what to do.
  if (selectedSampleIds.size === 0 && totalSampleCount > 0) {
    return (
      <Card>
        <div
          data-testid="pa-graph-no-selection"
          className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-heritage-grey"
        >
          <p className="font-semibold">No samples selected</p>
          <p className="text-xs">
            Re-check rows in the Table tab to plot data on the Graph.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <PassAnalyticsGraph
      samples={samples}
      selectedSampleIds={selectedSampleIds}
      sourceFilenames={sourceFilenames}
      toleranceBoundary={toleranceBoundary}
    />
  )
}
